import {Injectable, OnDestroy, inject} from '@angular/core';

import {Subscription, timer} from 'rxjs';
import {takeWhile} from 'rxjs/operators';

import {BuildLessonFromPdfAction} from '@actions/build-lesson-from-pdf.action';
import {LessonGenerationStore} from '@stores/lesson-generation.store';
import {LessonGenerationJob} from '@models/lesson-generation-types';
import {readingProgress} from '@utils/lesson-generation-utils';

// How often the estimated reading progress moves the bar
const PROGRESS_TICK_MS = 5_000;

/**
 * Builds the lessons queued in LessonGenerationStore (an uploaded PDF each) and records their
 * progress and result there. Builds outlive the dialog that started them; removing a job from
 * the store (closing its snackbar) stops it.
 */
@Injectable({providedIn: 'root'})
export class LessonGenerationReactor implements OnDestroy {
  private readonly _store = inject(LessonGenerationStore);
  private readonly _buildAction = inject(BuildLessonFromPdfAction);

  private readonly _runs = new Map<string, Subscription>();
  private readonly _subscription: Subscription;

  constructor() {
    this._subscription = this._store.state$.subscribe((jobs) => {
      for (const [id, run] of this._runs) {
        if (!jobs.some((j) => j.id === id)) {
          run.unsubscribe();
          this._runs.delete(id);
        }
      }
      for (const job of jobs) {
        if (job.status === 'queued' && !this._runs.has(job.id)) {
          this._build(job);
        }
      }
    });
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
    this._runs.forEach((run) => run.unsubscribe());
    this._runs.clear();
  }

  private _build(job: LessonGenerationJob) {
    // Registered before the store update below, which re-enters the subscription
    const run = new Subscription();
    this._runs.set(job.id, run);
    this._store.update(job.id, {status: 'processing'});

    const startedAt = Date.now();
    let reported = 0;
    const showReading = () =>
      this._store.progress(
        job.id,
        readingProgress(Date.now() - startedAt, reported),
      );

    run.add(
      timer(PROGRESS_TICK_MS, PROGRESS_TICK_MS)
        .pipe(takeWhile(() => reported < 1))
        .subscribe(showReading),
    );
    run.add(
      this._buildAction.execute(job.source).subscribe({
        next: (event) => {
          if ('reading' in event) {
            reported = Math.min(1, Math.max(reported, event.reading));
            showReading();
            return;
          }
          this._finish(job.id, {
            status: 'complete',
            progress: 100,
            hidden: false,
            lesson: event.lesson,
          });
        },
        error: (err: unknown) => {
          console.error('[LessonGeneration] Failed:', err);
          this._finish(job.id, {status: 'failed', hidden: false});
        },
      }),
    );
  }

  private _finish(
    id: string,
    changes: Partial<Omit<LessonGenerationJob, 'id'>>,
  ) {
    this._runs.get(id)?.unsubscribe();
    this._runs.delete(id);
    this._store.update(id, changes);
  }
}
